import React, { useState } from 'react';
import { styled } from '@compiled/react';
import Badge from '@atlaskit/badge';
import Heading from '@atlaskit/heading';
import { Card, CardAction, TitleBlock } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { CompassComponentLink } from '../../../../../common/services/search-compass-components/types.tsx';
import messages from '../messages.tsx';

export type Props = {
	repositories: CompassComponentLink[];
};

export const MAX_DISPLAYED_AMOUNT = 99;

const RepositoriesPopup = ({ repositories }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	if (repositories.length === 0) {
		return null;
	}

	const list = repositories.slice(0, MAX_DISPLAYED_AMOUNT);

	const content = () => (
		<ContentWrapper>
			<HeadingWrapper>
				<Heading size="xxsmall" as="div">
					{formatMessage(messages.repositories)}
				</Heading>
			</HeadingWrapper>

			{list.map((link) => (
				<Row key={`repository-link[${link.id}]`}>
					<CardContainer>
						<Card
							testId="project-directories.components.compass-table.cells.repository.repository-links-popup.smart-link"
							url={link.url}
							appearance="inline"
							placeholder={link.name || link.url}
							ui={{
								hideBackground: true,
								hideElevation: true,
								hidePadding: true,
								clickableContainer: true,
							}}
							onClick={() => {
								const analyticsEvent = createAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'link',
								});

								fireUIAnalytics(analyticsEvent, 'repositoryLink');
							}}
							actionOptions={{
								hide: false,
								exclude: [CardAction.ChangeStatusAction],
							}}
						>
							<TitleBlock text={link.name} maxLines={1} />
						</Card>
					</CardContainer>
				</Row>
			))}
		</ContentWrapper>
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="left-start"
			messageId="project-directories.components.compass-table.cells.repository.repository-links-popup.popup"
			messageType="transactional"
			content={content}
			trigger={(triggerProps) => (
				<BadgeContainer
					{...triggerProps}
					onClick={() => {
						setIsOpen(!isOpen);

						const analyticsEvent = createAnalyticsEvent({
							action: 'clicked',
							actionSubject: 'button',
						});

						fireUIAnalytics(analyticsEvent, 'repositoryLinkPopup');
					}}
				>
					<Badge
						testId="project-directories.components.compass-table.cells.repository.repository-links-popup.badge-button"
						appearance={!isOpen ? undefined : 'primaryInverted'}
					>
						+{list.length}
					</Badge>
				</BadgeContainer>
			)}
		/>
	);
};

export default RepositoriesPopup;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BadgeContainer = styled.span({
	cursor: 'pointer',
	marginTop: token('space.050'),
	marginRight: '0px',
	marginBottom: token('space.050'),
	marginLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover > span': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		backgroundColor: `${token('color.background.neutral.hovered')} !important`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	width: 222,
	height: '100%',
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.div({
	marginBottom: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		color: token('color.text'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardContainer = styled.div({
	paddingTop: 0,
	paddingRight: token('space.150'),
	paddingBottom: 0,
	paddingLeft: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Row = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: 222,
	height: 40,
	marginBottom: token('space.100'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),

	backgroundColor: token('color.background.accent.gray.subtlest'),
});
